import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, Building2, Home, Warehouse, HardHat, Clock, TrendingUp } from "lucide-react";
import { useCalculatorTracking } from "@/hooks/useCalculatorTracking";

type PropertyType = "residential" | "commercial" | "investment" | "construction";
type LoanPurpose = "purchase" | "refinance" | "bridge" | "cashout";
type Timeline = "asap" | "1-3months" | "3-6months" | "exploring";
type CreditProfile = "excellent" | "good" | "fair" | "challenged";

interface ServiceRecommendation {
  primary: string;
  secondary?: string;
  path: string;
  secondaryPath?: string;
  description: string;
}

const LoanPreQualifier = () => {
  const navigate = useNavigate();
  const { trackInteraction, trackCompletion } = useCalculatorTracking({
    calculatorType: "loan_prequalifier",
  });

  const [step, setStep] = useState(1);
  const [propertyType, setPropertyType] = useState<PropertyType | "">("");
  const [loanAmount, setLoanAmount] = useState("");
  const [loanPurpose, setLoanPurpose] = useState<LoanPurpose | "">("");
  const [timeline, setTimeline] = useState<Timeline | "">("");
  const [creditProfile, setCreditProfile] = useState<CreditProfile | "">("");
  const [recommendation, setRecommendation] = useState<ServiceRecommendation | null>(null);

  const totalSteps = 5;

  const formatCurrency = (value: string) => {
    const num = value.replace(/[^0-9]/g, "");
    if (!num) return "";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      maximumFractionDigits: 0,
    }).format(parseInt(num));
  };

  const parseCurrency = (value: string) => {
    return value.replace(/[^0-9]/g, "");
  };

  const getRecommendation = (): ServiceRecommendation => {
    const amount = parseInt(parseCurrency(loanAmount)) || 0;
    const isLargeCommercial = amount >= 1000000;
    const needsSpeed = timeline === "asap";
    const hasGoodCredit = creditProfile === "excellent" || creditProfile === "good";

    // Construction financing
    if (propertyType === "construction") {
      return {
        primary: "Construction Financing",
        path: "/services/construction-financing",
        description: "Specialized funding for ground-up construction and major renovations with flexible draw schedules.",
      };
    }

    // Bridge loan for urgent refinance/purchase
    if (needsSpeed && (loanPurpose === "bridge" || loanPurpose === "refinance")) {
      return {
        primary: "Bridge Loans",
        secondary: propertyType === "commercial" ? "Commercial Lending" : undefined,
        path: "/services/bridge-loans",
        secondaryPath: "/services/commercial",
        description: "Fast-close bridge financing for time-sensitive transactions. Funding in as little as 48 hours.",
      };
    }

    // Commercial properties
    if (propertyType === "commercial") {
      if (isLargeCommercial && hasGoodCredit) {
        return {
          primary: "A-Lending (Prime Rates)",
          secondary: "Commercial Lending",
          path: "/services/a-lending",
          secondaryPath: "/services/commercial",
          description: "Prime institutional rates for qualified commercial borrowers with strong financials.",
        };
      }
      if (!hasGoodCredit || needsSpeed) {
        return {
          primary: "Private Lending",
          secondary: "B-Lending",
          path: "/services/private-lending",
          secondaryPath: "/services/b-lending",
          description: "Flexible private capital solutions for commercial deals requiring speed or non-traditional underwriting.",
        };
      }
      return {
        primary: "DSCR Loans",
        secondary: "Commercial Lending",
        path: "/services/dscr-loans",
        secondaryPath: "/services/commercial",
        description: "Income-based commercial financing where the property's cash flow qualifies the loan.",
      };
    }

    // Investment properties
    if (propertyType === "investment") {
      if (hasGoodCredit && !needsSpeed) {
        return {
          primary: "DSCR Loans",
          secondary: "Investment Properties",
          path: "/services/dscr-loans",
          secondaryPath: "/services/investment-properties",
          description: "Qualify based on rental income rather than personal income. Ideal for portfolio investors.",
        };
      }
      if (needsSpeed) {
        return {
          primary: "Private Lending",
          secondary: "Bridge Loans",
          path: "/services/private-lending",
          secondaryPath: "/services/bridge-loans",
          description: "Quick private capital for investment opportunities that can't wait.",
        };
      }
      return {
        primary: "Investment Properties",
        path: "/services/investment-properties",
        description: "Specialized financing for rental properties, multi-family, and real estate investors.",
      };
    }

    // Residential properties
    if (propertyType === "residential") {
      if (hasGoodCredit) {
        return {
          primary: "A-Lending (Prime Rates)",
          secondary: "Residential Mortgages",
          path: "/services/a-lending",
          secondaryPath: "/services/residential",
          description: "Best rates from major lenders for well-qualified homebuyers.",
        };
      }
      if (creditProfile === "fair") {
        return {
          primary: "B-Lending",
          secondary: "Residential Mortgages",
          path: "/services/b-lending",
          secondaryPath: "/services/residential",
          description: "Alternative lending solutions for borrowers who don't fit traditional bank criteria.",
        };
      }
      return {
        primary: "Private Lending",
        secondary: "Residential Mortgages",
        path: "/services/private-lending",
        secondaryPath: "/services/residential",
        description: "Flexible private mortgage solutions for unique situations.",
      };
    }

    // Default
    return {
      primary: "Residential Mortgages",
      path: "/services/residential",
      description: "Comprehensive mortgage solutions for homebuyers across Canada.",
    };
  };

  const handleNext = () => {
    if (step < totalSteps) {
      trackInteraction(`step_${step}_completed`, {
        propertyType,
        loanAmount,
        loanPurpose,
        timeline,
        creditProfile,
      });
      setStep(step + 1);
    } else {
      const result = getRecommendation();
      setRecommendation(result);
      trackCompletion({
        propertyType,
        loanAmount: parseCurrency(loanAmount),
        loanPurpose,
        timeline,
        creditProfile,
        recommendation: result.primary,
      });
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return propertyType !== "";
      case 2:
        return loanAmount !== "" && parseInt(parseCurrency(loanAmount)) >= 50000;
      case 3:
        return loanPurpose !== "";
      case 4:
        return timeline !== "";
      case 5:
        return creditProfile !== "";
      default:
        return false;
    }
  };

  const propertyOptions = [
    { value: "residential", label: "Residential / Primary Home", icon: Home },
    { value: "investment", label: "Investment / Rental Property", icon: TrendingUp },
    { value: "commercial", label: "Commercial Property", icon: Building2 },
    { value: "construction", label: "Construction / Development", icon: HardHat },
  ];

  if (recommendation) {
    return (
      <Card className="w-full max-w-2xl mx-auto border-2 border-primary/20 shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Your Recommended Solution</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-6 border border-primary/20">
            <h3 className="text-xl font-bold text-primary mb-2">{recommendation.primary}</h3>
            <p className="text-muted-foreground">{recommendation.description}</p>
          </div>

          <div className="grid gap-3">
            <Button
              size="lg"
              className="w-full text-lg py-6"
              onClick={() => navigate(recommendation.path)}
            >
              Explore {recommendation.primary}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>

            {recommendation.secondary && recommendation.secondaryPath && (
              <Button
                variant="outline"
                size="lg"
                className="w-full"
                onClick={() => navigate(recommendation.secondaryPath!)}
              >
                Also see: {recommendation.secondary}
              </Button>
            )}

            <Button
              variant="ghost"
              onClick={() => {
                setStep(1);
                setRecommendation(null);
              }}
            >
              Start Over
            </Button>
          </div>

          <div className="text-center text-sm text-muted-foreground pt-4 border-t">
            <p>
              Want personalized advice?{" "}
              <a href="/contact" className="text-primary hover:underline font-medium">
                Book a free consultation
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-2xl mx-auto border-2 border-primary/20 shadow-xl">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between mb-2">
          <CardTitle className="text-xl">Find Your Best Financing Option</CardTitle>
          <span className="text-sm text-muted-foreground">
            Step {step} of {totalSteps}
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {step === 1 && (
          <div className="space-y-4">
            <Label className="text-lg font-semibold">What type of property are you financing?</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {propertyOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    onClick={() => setPropertyType(option.value as PropertyType)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      propertyType === option.value
                        ? "border-primary bg-primary/5"
                        : "border-muted-foreground/20 hover:border-primary/50"
                    }`}
                  >
                    <Icon
                      className={`h-6 w-6 mb-2 ${
                        propertyType === option.value ? "text-primary" : "text-muted-foreground"
                      }`}
                    />
                    <span className="font-medium">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <Label className="text-lg font-semibold">What is your target loan amount?</Label>
            <Input
              type="text"
              placeholder="$500,000"
              value={loanAmount}
              onChange={(e) => setLoanAmount(formatCurrency(e.target.value))}
              className="text-2xl h-14 font-semibold"
            />
            <p className="text-sm text-muted-foreground">
              Minimum $50,000. We finance deals from $50K to $50M+.
            </p>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <Label className="text-lg font-semibold">What is the purpose of this loan?</Label>
            <RadioGroup
              value={loanPurpose}
              onValueChange={(v) => setLoanPurpose(v as LoanPurpose)}
              className="space-y-3"
            >
              {[
                { value: "purchase", label: "Purchase a new property" },
                { value: "refinance", label: "Refinance existing mortgage" },
                { value: "bridge", label: "Bridge financing between properties" },
                { value: "cashout", label: "Cash-out for investment or renovation" },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    loanPurpose === option.value
                      ? "border-primary bg-primary/5"
                      : "border-muted-foreground/20 hover:border-primary/50"
                  }`}
                >
                  <RadioGroupItem value={option.value} className="mr-3" />
                  <span className="font-medium">{option.label}</span>
                </label>
              ))}
            </RadioGroup>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <Label className="text-lg font-semibold">How soon do you need funding?</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { value: "asap", label: "ASAP (within 2 weeks)", icon: "⚡" },
                { value: "1-3months", label: "1-3 months", icon: "📅" },
                { value: "3-6months", label: "3-6 months", icon: "🗓️" },
                { value: "exploring", label: "Just exploring options", icon: "🔍" },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTimeline(option.value as Timeline)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    timeline === option.value
                      ? "border-primary bg-primary/5"
                      : "border-muted-foreground/20 hover:border-primary/50"
                  }`}
                >
                  <span className="text-2xl mb-2 block">{option.icon}</span>
                  <span className="font-medium">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4">
            <Label className="text-lg font-semibold">How would you describe your credit profile?</Label>
            <Select value={creditProfile} onValueChange={(v) => setCreditProfile(v as CreditProfile)}>
              <SelectTrigger className="h-14 text-lg">
                <SelectValue placeholder="Select your credit situation" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="excellent">Excellent (720+) - Prime rates available</SelectItem>
                <SelectItem value="good">Good (680-719) - Competitive options</SelectItem>
                <SelectItem value="fair">Fair (620-679) - Alternative lenders</SelectItem>
                <SelectItem value="challenged">Challenged (below 620) - Private solutions</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Don't worry if your credit isn't perfect — we have solutions for every situation.
            </p>
          </div>
        )}

        <div className="flex gap-3 pt-4">
          {step > 1 && (
            <Button variant="outline" onClick={handleBack} className="flex-1">
              Back
            </Button>
          )}
          <Button
            onClick={handleNext}
            disabled={!canProceed()}
            className="flex-1"
          >
            {step === totalSteps ? "See My Recommendation" : "Continue"}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default LoanPreQualifier;
